const Rsa = require('node-rsa');
const cryptoJS = require('crypto-js');

class CommunicationEncryptHelper {

    /**
     * 判断接口类型
     * @param type 需要检测的类型
     * @param url 接口路由
     * @param method 请求方式
     */
    isRouteType(type: 'rsa' | 'aes' | 'status', url: string, method: string) {
        const w = window as any;
        const source = {
            rsa: w?.__ECT_R__?.rsaRouteList,
            aes: w?.__ECT_R__?.encryptRoutes,
            status: w?.__ECT_R__?.checkStatusRoute
        }[type];
        const upperMethod = method.toLocaleUpperCase();

        // 判断是否校验登录状态的接口，此地址需要根据当前页面中是否存在加密信息来决定是否进入密钥交换流程
        if (type === 'status') return source && url === source.path && upperMethod === source.methods;

        return !!(source || []).find((item: { path: string; methods: string; }) => {
            return item.path === url && item.methods === upperMethod;
        });
    }

    /**
     * 刷新前端加密接口
     * @param list 接口列表
     */
    refashRoutes(list: any[]) {
        const w = window as any;
        w.__ECT_R__.encryptRoutes = list;
    }

    /**
     * 参数加密
     * @param originParam 原始参数
     * @param url 接口 url
     */
    paramEncrypt(originParam: any, url: string, method: string) {
        // 页面中丢失 AES 信息时，通过 status 接口交换新密钥
        if (this.isRouteType('status', url, method)) {
            if (!(window as any).__secret_info__) return this.secretTransferAndEncrypt(originParam);
            return originParam;
        }
        if (originParam && this.isRouteType('rsa', url, method)) {
            return this.secretTransferAndEncrypt(originParam);
        }
        if (originParam && this.isRouteType('aes', url, method)) {
            return this.aesEncrypt(originParam);
        }
        return originParam;
    }

    /**
     * 响应解密
     * @param originParam 原始响应
     * @param url 接口 url
     */
    resDecrypt(originRes: any, url: string, method: string) {
        if (
            this.isRouteType('rsa', url, method)
            || this.isRouteType('aes', url, method)
            || this.isRouteType('status', url, method)
        ) {
            const { ciphertext } = originRes;
            if (!ciphertext) return originRes;
            return this.aesDecrypt(ciphertext);
        }
        return originRes;
    }

    /**
     * 对参数进行密钥交换的加密处理
     * @param originParam 原始参数
     */
    secretTransferAndEncrypt(originParam: any = {}) {
        if (window && (window as any).__ECT_P__) {
            // 公钥、aes 加密模式、密文短语
            const [pubKey, aesMethod, passphrase, ivphrase, pid] = (window as any).__ECT_P__.split(',');
            const eMethod = Buffer.from(aesMethod, 'base64').toString('utf8');
            // 密钥生成
            const encrypText = cryptoJS.AES.encrypt(JSON.stringify(originParam), cryptoJS.enc.Utf8.parse(passphrase), {
                iv: cryptoJS.enc.Utf8.parse(ivphrase),
                mode: cryptoJS.mode[eMethod]
            });
            const iv = encrypText.iv.toString();
            const key = encrypText.key.toString();
            // 密钥记录
            (window as any).__secret_info__ = {
                eMethod,
                iv,
                key,
            };
            // 刷新浏览器其它页面密钥信息
            (window as any).__bc_secret_info__?.postMessage((window as any).__secret_info__);
            const paramStr = JSON.stringify({ ...originParam, iv, key, mode: aesMethod });
            // https://github.com/orgs/vercel/discussions/6630#discussioncomment-9252173
            const rsa = new Rsa(pubKey, 'public', {
                encryptionScheme: 'pkcs1',
                environment: 'browser',
            });
            // 使用公钥进行加密
            const ciphertext = rsa.encrypt(paramStr, 'base64');
            // 进行 url 编码兼容 GET 参数
            return { ciphertext: `${encodeURIComponent(ciphertext)}_${pid}` };
        }
        return originParam;
    }

    /**
     * 参数对称加密
     * @param originParam 原始参数
     */
    aesEncrypt(originParam: any) {
        if (!(window as any).__secret_info__) return originParam;
        const { eMethod, iv, key } = (window as any).__secret_info__;
        if (!eMethod || !iv || !key) return originParam;
        try {
            const encrypText = cryptoJS.AES.encrypt(JSON.stringify(originParam), cryptoJS.enc.Hex.parse(key), {
                iv: cryptoJS.enc.Hex.parse(iv),
                mode: cryptoJS.mode[eMethod]
            });
            const ciphertext = encrypText.ciphertext.toString();
            return { ciphertext };
        } catch (error) {
            return originParam;
        }
    }

    /**
     * 响应对称解密
     * @param originRes 原始响应
     */
    aesDecrypt(originRes: any) {
        if (!(window as any).__secret_info__) return originRes;
        const { eMethod, iv, key } = (window as any).__secret_info__;
        if (!eMethod || !iv || !key) return originRes;
        try {
            const res = cryptoJS.AES.decrypt({ ciphertext: cryptoJS.enc.Hex.parse(originRes) }, cryptoJS.enc.Hex.parse(key), {
                iv: cryptoJS.enc.Hex.parse(iv),
                mode: cryptoJS.mode[eMethod],
                padding: cryptoJS.pad.NoPadding
            });
            const result = JSON.parse(res.toString(cryptoJS.enc.Utf8).replace(/[\u0000-\u001F\u0080-\u009F]/g, ''));
            return result;
        } catch (error) {
            return originRes;
        }
    }
}

export default new CommunicationEncryptHelper();
